const DEFAULT_COMMISSION_TIERS = Object.freeze([
  { minGold: 0, maxGold: 5, commissionFactor: 0.003 },
  { minGold: 5, maxGold: 20, commissionFactor: 0.002 },
  { minGold: 20, maxGold: null, commissionFactor: 0.001 },
]);

const DEFAULT_TRADE_SETTINGS = Object.freeze({
  buyPriceOffsetToman: 120000,
  sellPriceOffsetToman: 120000,
  commissionTiers: DEFAULT_COMMISSION_TIERS,
});

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeMaxGold = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export const normalizeCommissionTiers = (tiers) => {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    return DEFAULT_COMMISSION_TIERS.map((tier) => ({ ...tier }));
  }

  const normalized = tiers
    .map((tier) => {
      const minGold = toFiniteNumber(tier?.minGold, NaN);
      const commissionFactor = toFiniteNumber(tier?.commissionFactor, NaN);
      const maxGold = normalizeMaxGold(tier?.maxGold);

      if (!Number.isFinite(minGold) || minGold < 0) {
        return null;
      }

      if (!Number.isFinite(commissionFactor) || commissionFactor < 0) {
        return null;
      }

      if (maxGold !== null && maxGold <= minGold) {
        return null;
      }

      return {
        minGold,
        maxGold,
        commissionFactor,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.minGold - right.minGold);

  return normalized.length > 0
    ? normalized
    : DEFAULT_COMMISSION_TIERS.map((tier) => ({ ...tier }));
};

export const resolveTradeSettings = (source = null) => {
  const rawSettings = source || window.__appSettings || {};

  return {
    buyPriceOffsetToman: Math.max(
      0,
      toFiniteNumber(
        rawSettings.buyPriceOffsetToman,
        DEFAULT_TRADE_SETTINGS.buyPriceOffsetToman
      )
    ),
    sellPriceOffsetToman: Math.max(
      0,
      toFiniteNumber(
        rawSettings.sellPriceOffsetToman,
        DEFAULT_TRADE_SETTINGS.sellPriceOffsetToman
      )
    ),
    commissionTiers: normalizeCommissionTiers(rawSettings.commissionTiers),
  };
};

export const getCommissionFactorByGold = (goldAmount, settings = null) => {
  const gold = toFiniteNumber(goldAmount, 0);
  if (gold <= 0) {
    return 0;
  }

  const tradeSettings = resolveTradeSettings(settings);
  const matchedTier = tradeSettings.commissionTiers.find((tier) => {
    const withinMin = gold >= tier.minGold;
    const withinMax = tier.maxGold === null ? true : gold < tier.maxGold;
    return withinMin && withinMax;
  });

  return matchedTier ? matchedTier.commissionFactor : 0;
};

export const calculateQuotedTomanAmountFromGold = ({
  goldAmount,
  basePrice,
  symbol,
  settings = null,
}) => {
  const normalizedGold = toFiniteNumber(goldAmount, 0);
  const normalizedBasePrice = toFiniteNumber(basePrice, 0);

  if (normalizedGold <= 0 || normalizedBasePrice <= 0) {
    return 0;
  }

  const tradeSettings = resolveTradeSettings(settings);
  const rawAmount = normalizedGold * normalizedBasePrice;

  if (symbol === "IRRGLD") {
    return Math.max(0, Math.round(rawAmount + tradeSettings.buyPriceOffsetToman));
  }

  if (symbol === "GLDIRR") {
    return Math.max(0, Math.round(rawAmount - tradeSettings.sellPriceOffsetToman));
  }

  return Math.max(0, Math.round(rawAmount));
};

export const calculateGoldAmountFromQuotedToman = ({
  tomanAmount,
  basePrice,
  symbol,
  decimals = 3,
  settings = null,
}) => {
  const normalizedAmount = toFiniteNumber(tomanAmount, 0);
  const normalizedBasePrice = toFiniteNumber(basePrice, 0);

  if (normalizedAmount <= 0 || normalizedBasePrice <= 0) {
    return 0;
  }

  const tradeSettings = resolveTradeSettings(settings);
  let rawGold = 0;

  if (symbol === "IRRGLD") {
    const adjustedAmount = normalizedAmount - tradeSettings.buyPriceOffsetToman;
    rawGold = adjustedAmount > 0 ? adjustedAmount / normalizedBasePrice : 0;
  } else if (symbol === "GLDIRR") {
    rawGold =
      (normalizedAmount + tradeSettings.sellPriceOffsetToman) / normalizedBasePrice;
  } else {
    rawGold = normalizedAmount / normalizedBasePrice;
  }

  const precision = Number.isInteger(decimals) && decimals >= 0 ? decimals : 3;
  return Number(rawGold.toFixed(precision));
};

export const calculateDisplayTradePrices = (basePrice, settings = null) => {
  const normalizedBasePrice = toFiniteNumber(basePrice, 0);
  if (normalizedBasePrice <= 0) {
    return { buyPrice: 0, sellPrice: 0 };
  }

  const tradeSettings = resolveTradeSettings(settings);
  return {
    buyPrice: normalizedBasePrice + tradeSettings.buyPriceOffsetToman,
    sellPrice: Math.max(
      normalizedBasePrice - tradeSettings.sellPriceOffsetToman,
      0
    ),
  };
};

export const formatCommissionFactor = (commissionFactor) => {
  const normalizedFactor = toFiniteNumber(commissionFactor, 0);
  return normalizedFactor.toFixed(6).replace(/0+$/, "").replace(/\.$/, "") || "0";
};

export const formatCommissionTierLabel = (tier) => {
  const minGold = toFiniteNumber(tier?.minGold, 0);
  const maxGold = normalizeMaxGold(tier?.maxGold);

  if (maxGold === null) {
    return `از ${minGold} گرم به بالا`;
  }

  return `از ${minGold} تا ${maxGold} گرم`;
};

export { DEFAULT_COMMISSION_TIERS, DEFAULT_TRADE_SETTINGS };
